import React from 'react';
import type { SegmentDefinitionSummary } from '../segment-definitions-api';
import { SegmentOptionList } from './SegmentOptionList';
import { SegmentReuseWarning } from './SegmentReuseWarning';
import type { SegmentUsage, SegmentUsageMap } from './segment-usage';
import { EXCLUDE_INTRO, NO_EXCLUSION_PREFIX } from './segment-picker-copy';
import styles from './SegmentPicker.module.css';

const EXCLUSION_USAGE: SegmentUsage = { kind: 'exclusion' };

interface ExcludeSegmentPanelProps {
  segments: SegmentDefinitionSummary[];
  excludedIds: string[];
  onChange: (excludedIds: string[]) => void;
  includedId: string | null;
  includedTag: string;
  audienceDescription: string;
  usedElsewhere: SegmentUsageMap;
}

export const ExcludeSegmentPanel: React.FC<ExcludeSegmentPanelProps> = ({
  segments,
  excludedIds,
  onChange,
  includedId,
  includedTag,
  audienceDescription,
  usedElsewhere,
}) => {
  const noExclusionLabel = `${NO_EXCLUSION_PREFIX} ${audienceDescription}`;
  const hasExclusions = excludedIds.length > 0;
  const excludedSegments = segments.filter(segment => excludedIds.includes(segment.canonicalVersionId));
  const blockedIds = includedId ? [includedId] : [];

  const toggleExclusion = (canonicalVersionId: string) => {
    const isAlreadyExcluded = excludedIds.includes(canonicalVersionId);
    onChange(
      isAlreadyExcluded
        ? excludedIds.filter(id => id !== canonicalVersionId)
        : [...excludedIds, canonicalVersionId],
    );
  };

  return (
    <>
      <p className={styles.pickerIntro}>{EXCLUDE_INTRO}</p>
      <button
        type="button"
        className={styles.noExclusionOption}
        aria-pressed={!hasExclusions}
        onClick={() => onChange([])}>
        {noExclusionLabel}
      </button>
      {excludedSegments.map(({ canonicalVersionId, name }) => (
        <SegmentReuseWarning
          key={canonicalVersionId}
          segmentName={name}
          pickedUsage={EXCLUSION_USAGE}
          usagesElsewhere={usedElsewhere.get(canonicalVersionId)}
        />
      ))}
      <SegmentOptionList
        segments={segments}
        selectedIds={excludedIds}
        onSelect={toggleExclusion}
        usedElsewhere={usedElsewhere}
        variant="exclude"
        blocked={{ segmentIds: blockedIds, tag: includedTag }}
      />
    </>
  );
};
